import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { WebinarContainer } from '@generated/extended';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18nModule } from '@app/i18n';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

interface News {
  id: number;
  url: string;
  language: string;
}

interface EventContainer {
  url: string;
  videoLink?: string;
  title: string;
  description: string;
  date: string;
  language: string;
}

@UntilDestroy()
@Component({
  selector: 'app-webinar',
  standalone: true,
  imports: [TranslocoModule, CommonModule, I18nModule, MatIconModule],
  templateUrl: './webinar.component.html',
  styleUrls: ['./webinar.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['products'],
    },
  ],
})
export class WebinarComponent implements OnInit {
  private _translocoService = inject(TranslocoService);
  private router = inject(Router);

  private langKey = signal<string>('de');

  webinars: WebinarContainer[] = [
    {
      url: 'https://kv-02-w26d.events.sia.ch/#E41234',
      date: '20.10.2026',
      alias: 'all-calculators',
      language: 'de',
    },
    {
      url: 'https://calculateurchargeshonoraires.events.sia.ch/#EF9A42',
      date: '25.06.2026',
      alias: 'effort-planner',
      language: 'fr',
    },
    {
      url: 'https://constructionsneuves.ai.events.sia.ch/#EA7507',
      date: '29.10.2026',
      alias: 'building-costs',
      language: 'fr',
    },
    {
      url: 'https://renovationettransformation.events.sia.ch/#E41234',
      date: '05.11.2026',
      alias: 'rebuild-costs',
      language: 'fr',
    },
    // building-costs https://digi-neubaukosten.events.sia.ch/
    // rebuild-costs https://digitalisierte-umbaukostenschaetzungen.events.sia.ch/
  ];

  events: EventContainer[] = [
    {
      url: 'https://www.architectatwork.com/fr/events/a@w-lausanne',
      title: 'Architect@Work | 30 Sept. & 01 Oct.',
      description: 'Venez découvrir les dernières innovations pour les architectes.',
      date: '01.10.2026',
      language: 'fr',
    },
    {
      url: 'https://www.fmpro-swiss.ch/fr/networking/evenement-du-reseau-romandie-conferences-sur-les-couts-du-cycle-de-vie',
      title: 'Événement du Réseau Romandie - conférences sur les coûts du cycle de vie | 11 Juin',
      description:
        'Optimisez la rentabilité de vos biens en participant à la présentation de nos outils du cycle de vie LCC dans le cadre d’un évènement organisé par fmpro sur les coûts d’exploitation.',
      date: '11.06.2026',
      language: 'fr',
    },
  ];

  private news: News[] = [
    {
      id: 1,
      url: `pdf/${this.langKey().toLowerCase()}/keeValue_nzz.pdf`,
      language: 'de',
    },
    {
      id: 2,
      url: 'https://www.swissbau.ch/de/c/lebenszykluskosten-praezision-durch-kuenstliche-intelligenz.43791',
      language: 'de',
    },
    {
      id: 3,
      url: `pdf/${this.langKey().toLowerCase()}/keeValue_LCA.pdf`,
      language: 'de',
    },
  ];

  ngOnInit(): void {
    this.updateLanguage();
  }

  getUpcoming<T extends { date: string; language: string }>(items: T[], limit?: number): T[] {
    const today = new Date();
    return items
      .filter((item) => {
        const [day, month, year] = item.date.split('.');
        const itemDate = new Date(`${year}-${month}-${day}`);
        return item.language === this.langKey() && itemDate >= today;
      })
      .sort((a, b) => {
        const [dayA, monthA, yearA] = a.date.split('.');
        const [dayB, monthB, yearB] = b.date.split('.');
        return new Date(`${yearA}-${monthA}-${dayA}`).getTime() - new Date(`${yearB}-${monthB}-${dayB}`).getTime();
      })
      .slice(0, limit ?? items.length);
  }

  getNews = computed(() => {
    return this.news.filter((news) => news.language === this.langKey());
  });

  pdfLanguagePath = computed(() => {
    return this.langKey().toLowerCase();
  });

  openCO2Pdf(event: Event) {
    event.preventDefault();
    const pdfPath = `pdf/${this.pdfLanguagePath()}/keeValue_CO2.pdf`;

    fetch(pdfPath, { method: 'HEAD' })
      .then((response) => {
        if (response.ok) {
          window.open(pdfPath, '_blank');
        } else {
          this.router.navigate(['/404']);
        }
      })
      .catch(() => {
        this.router.navigate(['/404']);
      });
  }

  updateLanguage() {
    this._translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (lang) => {
        const [langKey] = lang.split('-');
        this.langKey.set(langKey);
      },
    });
  }

  isDateInPast(dateString: string): boolean {
    const [day, month, year] = dateString.split('.');
    const webinarDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    return webinarDate < today;
  }
}
